<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { FILTER_MESSAGES, EMPTY_MESSAGES } from '@Constants';
import { useAnimalProfileStore, useSessionStore } from '@Stores';

const { groupEndpoint, groups } = storeToRefs(useAnimalProfileStore());
const { shelterProfile } = storeToRefs(useSessionStore());
const { fetchGroupsService } = useAnimalProfileStore();

const selectedGroup = defineModel<string>();
const loading = ref(false);

const options = computed(() => {
  return (
    groups?.value?.data?.map((group: any) => ({
      name: group.seq_id + ' - ' + group.label,
      value: group._id.$oid,
    })) ?? []
  ).sort((a: any, b: any) => b.name.localeCompare(a.name));
});
const hasGroupsEnabled = computed(() => shelterProfile.value?.enable_groups_litters);

onMounted(() => getGroupsOptions());

async function getGroupsOptions() {
  loading.value = true;
  groupEndpoint.value = 'active';

  await fetchGroupsService();
  loading.value = false;
}
</script>

<template>
  <FloatLabel class="mt-2 w-full">
    <MultiSelect
      v-bind="{ FILTER_MESSAGES, EMPTY_MESSAGES }"
      v-model="selectedGroup"
      display="chip"
      optionLabel="name"
      optionValue="value"
      pt:root="w-full"
      pt:emptyMessage="text-xs"
      pt:filterInput="h-9 py-1 pl-3 text-xs"
      pt:headerCheckbox:box="size-5"
      pt:itemCheckbox:box="size-5"
      pt:labelContainer="h-9 p-1 pr-7 text-xs flex items-center"
      pt:panel="sm:w-[265px] md:w-[330px] lg:w-[268px]"
      pt:token="rounded text-sm py-1 px-2"
      pt:option="text-sm"
      :options
      :disabled="loading || !hasGroupsEnabled"
      :filter="true"
      :selected="true"
    >
      <template #value="{ value }">
        <template v-if="value?.length">
          <span
            v-for="(selectedValue, index) in value"
            class="mr-1 rounded-md !bg-brand-100 px-2 py-1 font-medium text-gray-600"
            :key="index"
          >
            {{
              $t(
                options.find((optionItem: any) => optionItem.value === selectedValue)?.name ||
                  selectedValue
              )
            }}
          </span>
        </template>

        <span
          v-else
          class="ml-2 w-[90%] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ $t('filters.label.group') }}
        </span>
      </template>

      <template #option="{ option }">
        <span class="text-xs">
          {{ $t(option.name) }}
        </span>
      </template>

      <slot />
    </MultiSelect>

    <label
      class="-mt-2.5 flex w-[90%] gap-2 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-zinc-500"
    >
      <span class="ml-2 w-[90%] overflow-hidden text-ellipsis whitespace-nowrap">
        {{ $t('filters.label.group') }}
      </span>
    </label>
  </FloatLabel>
</template>
